<template>
  <div class="auth-container">
    <div class="auth-box">
      <template v-if="!registrationComplete">
        <h2>Create Account</h2>
        <p class="subtitle">Please enter only test/sample data.</p>
      </template>

      <form v-if="!registrationComplete" @submit.prevent="handleRegister">
        <div class="form-row">
          <div class="form-group">
            <label for="username">Username</label>
            <div class="input-wrapper">
              <input
                id="username"
                ref="usernameInput"
                type="text"
                v-model="formData.username"
                placeholder="Enter username"
                autocomplete="username"
                required
                :class="{
                  error: errors.username,
                  checking: isCheckingUsername,
                }"
                @blur="checkUsernameAvailability"
              />
              <div v-if="isCheckingUsername" class="loading-spinner">
                <div class="spinner"></div>
              </div>
            </div>
            <span v-if="isCheckingUsername" class="checking-message"
              >Checking availability...</span
            >
            <span v-if="errors.username" class="error-message">{{
              errors.username
            }}</span>
          </div>
        </div>

        <div class="form-row">
          <div class="form-group">
            <label for="email">Email Address</label>
            <input
              id="email"
              type="email"
              v-model="formData.email"
              placeholder="your.email@example.com"
              autocomplete="email"
              required
              :class="{ error: errors.email }"
            />
            <span v-if="errors.email" class="error-message">{{
              errors.email
            }}</span>
          </div>
        </div>

        <div class="form-row">
          <div class="form-group">
            <label for="dateOfBirth">Date of Birth</label>
            <input
              id="dateOfBirth"
              type="text"
              v-model="formData.dateOfBirth"
              placeholder="MM / DD / YYYY"
              autocomplete="bday"
              required
              :class="{ error: errors.dateOfBirth }"
              @input="formatDateOfBirth"
              maxlength="14"
            />
            <span v-if="errors.dateOfBirth" class="error-message">{{
              errors.dateOfBirth
            }}</span>
            <small class="help-text"
              >Enter in format: MM / DD / YYYY (e.g., 01 / 15 / 1990)</small
            >
          </div>
        </div>

        <div class="form-row">
          <div class="form-group">
            <label for="password">Password</label>
            <input
              id="password"
              type="text"
              value="password123"
              readonly
              disabled
              class="static-field"
            />
            <small class="help-text"
              >Demo password (this cannot be changed)</small
            >
          </div>
        </div>

        <div v-if="registrationError" class="registration-error">
          {{ registrationError }}
        </div>

        <button
          type="submit"
          class="auth-button"
          :disabled="isSubmitting || isUsernameTaken"
        >
          {{ isSubmitting ? "Creating Account..." : "Create Account" }}
        </button>
      </form>

      <div v-else class="post-register-choice">
        <h2>Verify your identity</h2>
        <p class="subtitle">
          Verify your identity now to unlock payments and withdrawals — or skip
          for now and we'll prompt you later.
        </p>
        <button class="auth-button" @click="$router.push('/verify')">
          Verify Identity Now
        </button>
        <button class="auth-button secondary" @click="$router.push('/games')">
          Skip for Now
        </button>
      </div>

      <div v-if="!registrationComplete" class="auth-links">
        <p>
          Already have an account?
          <router-link to="/login">Sign in</router-link>
        </p>
      </div>

      <div v-if="message" class="message" :class="messageType">
        {{ message }}
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import { getVivamoCustomer, createVivamoCustomer } from "@/api/vivamo-api";

export default {
  name: "Register",
  data() {
    return {
      formData: {
        username: "",
        email: "",
        dateOfBirth: "",
      },
      errors: {},
      message: "",
      messageType: "",
      registrationError: "",
      isSubmitting: false,
      isCheckingUsername: false,
      isUsernameTaken: false,
      registrationComplete: false,
      demoPassword: "password123",
    };
  },
  mounted() {
    this.$nextTick(() => {
      if (this.$refs.usernameInput) {
        this.$refs.usernameInput.focus();
      }
    });
  },
  computed: {
    canSubmitForm() {
      return (
        !this.isSubmitting && !this.isUsernameTaken && !this.isCheckingUsername
      );
    },
  },
  watch: {
    "formData.username"() {
      // Reset username taken status when user types
      if (this.isUsernameTaken) {
        this.isUsernameTaken = false;
        if (
          this.errors.username === "That username is taken, please use another"
        ) {
          delete this.errors.username;
        }
      }
    },
  },
  methods: {
    validateForm() {
      this.errors = {};
      let isValid = true;

      // Username validation
      if (!this.formData.username.trim()) {
        this.errors.username = "Username is required";
        isValid = false;
      } else if (this.formData.username.length < 3) {
        this.errors.username = "Username must be at least 3 characters";
        isValid = false;
      } else if (!/^[a-zA-Z0-9_-]+$/.test(this.formData.username)) {
        this.errors.username =
          "Username can only contain letters, numbers, hyphens, and underscores";
        isValid = false;
      } else if (this.isUsernameTaken) {
        this.errors.username = "That username is taken, please use another";
        isValid = false;
      }

      // Email validation
      if (!this.formData.email.trim()) {
        this.errors.email = "Email address is required";
        isValid = false;
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.formData.email)) {
        this.errors.email = "Please enter a valid email address";
        isValid = false;
      }

      // Date of birth validation
      if (!this.formData.dateOfBirth.trim()) {
        this.errors.dateOfBirth = "Date of birth is required";
        isValid = false;
      } else {
        const dobValidation = this.validateDateOfBirth(
          this.formData.dateOfBirth
        );
        if (!dobValidation.isValid) {
          this.errors.dateOfBirth = dobValidation.error;
          isValid = false;
        }
      }

      return isValid;
    },

    formatDateOfBirth() {
      // Remove all non-numeric characters
      let value = this.formData.dateOfBirth.replace(/\D/g, "");

      // Limit to 8 digits max (MMDDYYYY)
      if (value.length > 8) {
        value = value.substring(0, 8);
      }

      // Apply MM/DD/YYYY mask
      let formatted = "";
      if (value.length >= 1) {
        formatted = value.substring(0, 2);
      }
      if (value.length >= 3) {
        formatted += " / " + value.substring(2, 4);
      }
      if (value.length >= 5) {
        formatted += " / " + value.substring(4, 8);
      }

      this.formData.dateOfBirth = formatted;
    },

    validateDateOfBirth(dateString) {
      // Regular expression for "MM / DD / YYYY" format
      const regex = /^(\d{2})\s*\/\s*(\d{2})\s*\/\s*(\d{4})$/;
      const match = dateString.trim().match(regex);

      if (!match) {
        return {
          isValid: false,
          error:
            "Please enter date in format: MM / DD / YYYY (e.g. 01 / 15 / 1990)",
        };
      }

      const month = parseInt(match[1]);
      const day = parseInt(match[2]);
      const year = parseInt(match[3]);

      // Validate the date components
      if (year < 1900 || year > new Date().getFullYear()) {
        return {
          isValid: false,
          error: "Please enter a valid year between 1900 and current year",
        };
      }

      if (month < 1 || month > 12) {
        return {
          isValid: false,
          error: "Please enter a valid month (01-12)",
        };
      }

      if (day < 1 || day > 31) {
        return {
          isValid: false,
          error: "Please enter a valid day (01-31)",
        };
      }

      // Create date object to validate if the date actually exists (month is 0-based in Date constructor)
      const dateObj = new Date(year, month - 1, day);
      if (
        dateObj.getMonth() !== month - 1 ||
        dateObj.getDate() !== day ||
        dateObj.getFullYear() !== year
      ) {
        return {
          isValid: false,
          error: "Please enter a valid date",
        };
      }

      // Calculate age
      const today = new Date();
      let age = today.getFullYear() - year;
      const monthDiff = today.getMonth() - (month - 1);
      const dayDiff = today.getDate() - day;

      // Adjust age if birthday hasn't occurred this year
      if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
        age--;
      }

      // Check if user is 18 or older
      if (age < 21) {
        return {
          isValid: false,
          error: "You must be 21 or older to use this site",
        };
      }

      return {
        isValid: true,
        dateObj: dateObj,
        formattedDate: `${year}-${String(month).padStart(2, "0")}-${String(
          day
        ).padStart(2, "0")}`,
      };
    },

    showMessage(text, type = "info") {
      this.message = text;
      this.messageType = type;
      setTimeout(() => {
        this.message = "";
        this.messageType = "";
      }, 5000);
    },

    async checkUsernameAvailability() {
      const username = this.formData.username.trim();

      // Reset username status
      this.isUsernameTaken = false;
      if (
        this.errors.username === "That username is taken, please use another"
      ) {
        delete this.errors.username;
      }

      // Only check if username meets basic requirements
      if (
        !username ||
        username.length < 3 ||
        !/^[a-zA-Z0-9_-]+$/.test(username)
      ) {
        return;
      }

      this.isCheckingUsername = true;

      try {
        // Check if customer exists with this username
        const customer = await getVivamoCustomer(username);

        if (customer) {
          // Customer exists (username is taken)
          this.isUsernameTaken = true;
          this.errors.username = "That username is taken, please use another";
        } else {
          // Customer not found (username is available)
          this.isUsernameTaken = false;
        }
      } catch (error) {
        // For unexpected errors, assume username is available but log the error
        console.warn("Error checking username availability:", error);
        this.isUsernameTaken = false;
      } finally {
        this.isCheckingUsername = false;
      }
    },

    async handleRegister() {
      // Clear any previous registration error
      this.registrationError = "";

      // Check if form can be submitted (same logic as button disabled state)
      if (!this.canSubmitForm) {
        if (this.isCheckingUsername) {
          this.showMessage(
            "Please wait while we check username availability",
            "info"
          );
        } else if (this.isUsernameTaken) {
          this.showMessage(
            "Please choose a different username before submitting",
            "error"
          );
        } else if (this.isSubmitting) {
          // Already submitting, do nothing
          return;
        }
        return;
      }

      if (!this.validateForm()) {
        this.showMessage("Please correct the errors above", "error");
        return;
      }

      this.isSubmitting = true;

      try {
        // Validate and format date of birth
        const dobValidation = this.validateDateOfBirth(
          this.formData.dateOfBirth
        );
        if (!dobValidation.isValid) {
          this.errors.dateOfBirth = dobValidation.error;
          this.showMessage("Please correct the errors above", "error");
          this.isSubmitting = false;
          return;
        }

        // Create Vivamo customer
        const customerData = {
          username: this.formData.username,
          email: this.formData.email,
          dateOfBirth: dobValidation.formattedDate,
        };

        const vivamoResponse = await createVivamoCustomer(customerData);
        console.log("Vivamo customer created:", vivamoResponse);

        // Store user data locally for demo purposes
        const userData = {
          username: this.formData.username,
          email: this.formData.email,
          registeredAt: new Date().toISOString(),
          vivamoCustomerId: vivamoResponse.id || vivamoResponse._id,
        };
        localStorage.setItem("demoUserData", JSON.stringify(userData));

        // Log the new user in straight away — verification is optional and the
        // SDK will force KYC at payment/disbursement time if still pending.
        this.$store.dispatch("login");

        this.registrationComplete = true;
      } catch (error) {
        console.error("Registration error:", error);

        // Display API error message if available, otherwise show generic message
        const genericErrorMessage =
          "Your registration failed, please check your details and try again.";
        this.registrationError = error.apiMessage || genericErrorMessage;

        // Handle specific API errors
        if (
          error.statusCode === 409 ||
          error.message.includes("409") ||
          error.message.includes("Conflict")
        ) {
          this.isUsernameTaken = true;
          this.errors.username = "That username is taken, please use another";
        }
      } finally {
        this.isSubmitting = false;
      }
    },
  },
};
</script>

<style scoped>
.auth-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  padding: 20px;
}

.auth-box {
  background: var(--card-background);
  padding: 2rem;
  border-radius: 15px;
  width: 100%;
  max-width: 600px;
  border: 1px solid var(--accent-color);
}

.auth-box h2 {
  text-align: center;
  margin-bottom: 0.5rem;
  color: var(--text-color);
}

.subtitle {
  text-align: center;
  color: var(--accent-color);
  margin-bottom: 2rem;
  font-size: 0.9rem;
}

.form-row {
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
}

.form-row .form-group {
  flex: 1;
}

.form-group {
  margin-bottom: 1rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  color: var(--text-color);
  font-weight: 500;
  font-size: 0.9rem;
}

.input-wrapper {
  position: relative;
  width: 100%;
}

input,
select {
  width: 100%;
  padding: 0.8rem;
  border: 1px solid var(--primary-color);
  border-radius: 8px;
  background-color: var(--input-background);
  color: var(--text-color);
  font-size: 0.9rem;
  transition: border-color 0.3s ease;
  box-sizing: border-box;
}

input::placeholder {
  color: var(--input-placeholder);
}

input:focus,
select:focus {
  outline: none;
  border-color: var(--accent-color);
  box-shadow: 0 0 0 2px rgba(218, 164, 93, 0.2);
}

input.error,
select.error {
  border-color: #ef4444;
  box-shadow: 0 0 0 2px rgba(239, 68, 68, 0.2);
}

input.checking {
  border-color: #3b82f6;
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.2);
  padding-right: 2.5rem;
}

.loading-spinner {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
}

.spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(59, 130, 246, 0.3);
  border-top: 2px solid #3b82f6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.checking-message {
  color: #3b82f6;
  font-size: 0.8rem;
  margin-top: 0.25rem;
  display: block;
  font-style: italic;
}

select {
  cursor: pointer;
}

select option {
  background-color: var(--card-background);
  color: var(--text-color);
}

.static-field {
  background-color: var(--muted-field-background) !important;
  color: var(--muted-field-color) !important;
  cursor: not-allowed !important;
  font-weight: 500;
  border-color: rgba(218, 164, 93, 0.3) !important;
}

.static-field:focus {
  border-color: rgba(218, 164, 93, 0.3) !important;
  box-shadow: none !important;
}

.error-message {
  color: #ef4444;
  font-size: 0.8rem;
  margin-top: 0.25rem;
  display: block;
}

.help-text {
  color: var(--accent-color);
  font-size: 0.75rem;
  margin-top: 0.25rem;
  display: block;
  opacity: 0.8;
}

.registration-error {
  background: rgba(239, 68, 68, 0.1);
  color: #ef4444;
  border: 1px solid rgba(239, 68, 68, 0.3);
  border-radius: 8px;
  padding: 0.75rem;
  margin-bottom: 1rem;
  font-size: 0.9rem;
  font-weight: 500;
  text-align: center;
}

.auth-button {
  width: 100%;
  padding: 1rem;
  background-color: var(--secondary-color);
  color: white;
  border: none;
  border-radius: 25px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 1rem;
  font-weight: 600;
  margin-top: 0.5rem;
}

.auth-button:hover:not(:disabled) {
  background-color: var(--accent-color);
  color: var(--primary-color);
  transform: translateY(-2px);
}

.auth-button.secondary {
  background-color: transparent;
  color: var(--accent-color);
  border: 1px solid var(--accent-color);
  margin-top: 0.75rem;
}

.auth-button.secondary:hover:not(:disabled) {
  background-color: rgba(218, 164, 93, 0.1);
  color: var(--accent-color);
}

.post-register-choice {
  text-align: center;
}

.post-register-choice h2 {
  color: var(--text-color);
  margin-bottom: 0.5rem;
}

.auth-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.auth-links {
  text-align: center;
  margin-top: 1.5rem;
  padding-top: 1.5rem;
  border-top: 1px solid var(--input-border);
}

.auth-links p {
  color: var(--text-color);
  margin: 0;
}

.auth-links a {
  color: var(--accent-color);
  text-decoration: none;
  font-weight: 500;
  transition: color 0.3s ease;
}

.auth-links a:hover {
  color: var(--secondary-color);
  text-decoration: underline;
}

.message {
  margin-top: 1rem;
  padding: 0.75rem;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 500;
  text-align: center;
}

.message.success {
  background: rgba(16, 185, 129, 0.1);
  color: #10b981;
  border: 1px solid rgba(16, 185, 129, 0.3);
}

.message.error {
  background: rgba(239, 68, 68, 0.1);
  color: #ef4444;
  border: 1px solid rgba(239, 68, 68, 0.3);
}

.message.info {
  background: rgba(59, 130, 246, 0.1);
  color: #3b82f6;
  border: 1px solid rgba(59, 130, 246, 0.3);
}

/* Mobile responsive styles */
@media (max-width: 768px) {
  .auth-container {
    padding: 1rem;
    align-items: flex-start;
    padding-top: 2rem;
  }

  .auth-box {
    padding: 1.5rem;
    width: 100%;
    max-width: none;
    border-radius: 12px;
  }

  .auth-box h2 {
    font-size: 1.8rem;
    margin-bottom: 0.75rem;
  }

  .subtitle {
    font-size: 0.85rem;
    margin-bottom: 1.5rem;
  }

  .form-row {
    flex-direction: column;
    gap: 0;
    margin-bottom: 0.8rem;
  }

  .form-row .form-group {
    margin-bottom: 1rem;
  }

  .form-group {
    margin-bottom: 1rem;
  }

  .form-group label {
    font-size: 0.85rem;
    margin-bottom: 0.4rem;
  }

  input,
  select {
    padding: 1rem;
    font-size: 1rem;
    border-radius: 10px;
  }

  input.checking {
    padding-right: 3rem;
  }

  .loading-spinner {
    right: 15px;
  }

  .spinner {
    width: 18px;
    height: 18px;
  }

  .error-message {
    font-size: 0.75rem;
  }

  .help-text {
    font-size: 0.7rem;
  }

  .checking-message {
    font-size: 0.75rem;
  }

  .auth-button {
    padding: 1.2rem;
    font-size: 1rem;
    border-radius: 30px;
    margin-top: 1rem;
  }

  .auth-links {
    margin-top: 1.2rem;
    padding-top: 1.2rem;
  }

  .auth-links p {
    font-size: 0.9rem;
  }

  .message {
    font-size: 0.85rem;
    padding: 0.8rem;
  }

  .registration-error {
    font-size: 0.85rem;
    padding: 0.8rem;
  }
}

@media (max-width: 480px) {
  .auth-container {
    padding: 0.5rem;
    padding-top: 1rem;
  }

  .auth-box {
    padding: 1.2rem;
    border-radius: 8px;
  }

  .auth-box h2 {
    font-size: 1.6rem;
  }

  .subtitle {
    font-size: 0.8rem;
    margin-bottom: 1.2rem;
  }

  .form-group {
    margin-bottom: 0.8rem;
  }

  .form-group label {
    font-size: 0.8rem;
    margin-bottom: 0.3rem;
  }

  input,
  select {
    padding: 0.9rem;
    font-size: 0.95rem;
    border-radius: 8px;
  }

  input.checking {
    padding-right: 2.8rem;
  }

  .loading-spinner {
    right: 12px;
  }

  .spinner {
    width: 16px;
    height: 16px;
  }

  .error-message {
    font-size: 0.7rem;
  }

  .help-text {
    font-size: 0.65rem;
  }

  .checking-message {
    font-size: 0.7rem;
  }

  .auth-button {
    padding: 1rem;
    font-size: 0.95rem;
    border-radius: 25px;
  }

  .message {
    font-size: 0.8rem;
    padding: 0.7rem;
  }

  .registration-error {
    font-size: 0.8rem;
    padding: 0.7rem;
  }
}

/* Landscape mobile orientation */
@media (max-width: 768px) and (orientation: landscape) {
  .auth-container {
    padding-top: 1rem;
    min-height: auto;
  }

  .auth-box {
    margin: 0;
  }

  .form-row {
    flex-direction: row;
    gap: 1rem;
  }

  .form-row .form-group {
    flex: 1;
    margin-bottom: 0.8rem;
  }
}

/* Very small screens */
@media (max-width: 320px) {
  .auth-box {
    padding: 1rem;
  }

  .auth-box h2 {
    font-size: 1.4rem;
  }

  input,
  select {
    padding: 0.8rem;
    font-size: 0.9rem;
  }

  .auth-button {
    padding: 0.9rem;
    font-size: 0.9rem;
  }
}
</style>
